import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import {
  DxButtonModule,
  DxDataGridModule,
  DxDropDownBoxComponent,
  DxDropDownBoxModule,
  DxFormModule,
  DxSelectBoxModule,
  DxTextBoxModule,
} from 'devextreme-angular';
import { SingleMultipleAllOrNone } from 'devextreme/common';
import DataSource from 'devextreme/data/data_source';
import * as lpn from 'google-libphonenumber';
import { AbcDropDownBoxComponent } from '../abc-drop-down-box/abc-drop-down-box.component';
import { AbcListComponent } from '../abc-list/abc-list.component';
import { AbcNumberBoxComponent } from '../abc-number-box/abc-number-box.component';
import { _AbcSelectBoxComponent } from '../abc-select-box/abc-select-box.component';
import { AbcTextBoxComponent } from '../abc-text-box/abc-text-box.component';
import { CountryCode } from './country-code';
import { CountryISO } from './enums/country-iso.enum';
import { ChangeData, Country } from './interfaces/change-data';

@Component({
  standalone: true,
  selector: 'abc-phone-number',
  templateUrl: './abc-phone-number.component.html',
  styleUrls: ['./abc-phone-number.component.scss'],
  imports: [
    AbcTextBoxComponent,
    AbcDropDownBoxComponent,
    FlexLayoutModule,
    DxButtonModule,
    DxSelectBoxModule,
    DxFormModule,
    DxTextBoxModule,
    DxDropDownBoxModule,
    AbcNumberBoxComponent,
    _AbcSelectBoxComponent,
    DxSelectBoxModule,
    MatIconModule,
    DxDataGridModule,
    AbcListComponent,
  ],
  providers: [
    CountryCode,
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => AbcNumberBoxComponent),
    },
  ],
})
export class AbcPhoneNumberComponent implements OnInit {
  @ViewChild('selectContrydropdown') selectContrydropdown: DxDropDownBoxComponent;
  @ViewChild('abcList', { static: false }) abcList: AbcListComponent;
  @Input() value = '';
  @Input() onlyCountries: CountryISO[] = [];
  @Input() selectionMode: SingleMultipleAllOrNone = 'single';
  private _dialCode: string = '+49';
  @Input()
  set dialCode(value: string) {
    this._dialCode = value || '+49';
    this.handleCountryValue();
    this.onCountrySelect();
  }
  get dialCode(): string {
    return this._dialCode;
  }
  @Output() onValueChanged: EventEmitter<ChangeData> = new EventEmitter<ChangeData>();
  public allCountries: Array<Country> = [];
  public countriesListStore: DataSource<Country, string>;
  public mask: string;
  public selectedCountry: Country;
  private phoneUtil: lpn.PhoneNumberUtil = lpn.PhoneNumberUtil.getInstance();
  private phoneNumberInformations: ChangeData;
  private selectedCountryIndex: number;
  constructor(private countryCodeData: CountryCode) {}

  ngOnInit(): void {
    if (this.onlyCountries?.length === 0) {
      this.getAllCountries();
    } else {
      this.allCountries = [];
      this.onlyCountries?.forEach((element) => {
        const selectedCountryIndex = this.countryCodeData.allCountries.findIndex((country) => country[1] === element);
        const country = this.getCountryInformations(
          this.countryCodeData.allCountries[selectedCountryIndex],
          selectedCountryIndex
        );
        this.allCountries.push(country);
      });
    }
    this.countriesListStore = new DataSource({
      pageSize: this.allCountries.length,
      store: {
        type: 'array',
        data: this.allCountries,
      },
    });
    this.handleCountryValue();
    this.onCountrySelect();
  }

  onOpened() {
    setTimeout(() => {
      this.abcList?.instance.scrollToItem(this.selectedCountryIndex);
    }, 10);
  }

  private getAllCountries(): void {
    this.countryCodeData.allCountries.forEach((country, index) => {
      const countryInformations = this.getCountryInformations(country, index);
      this.allCountries.push(countryInformations);
    });
  }

  private getCountryInformations(country, index: number): Country {
    const countryInformations: Country = {
      name: country[0].toString(),
      iso2: country[1].toString(),
      dialCode: '+' + country[2].toString(),
      priority: country[3] || 0,
      areaCodes: (country[4] as string[]) || undefined,
      htmlId: `iti-0__item-${country[1].toString()}`,
      placeHolder: '',
      flag: 'assets/images/flags/' + country[1].toString() + '.svg',
      id: index,
    };
    return countryInformations;
  }

  public onPhoneNumberChange(event): void {
    let countryCode: string | undefined;
    this.value = event.value;
    countryCode = countryCode || this.selectedCountry.iso2;
    const number = this.getParsedNumber(this.value, countryCode);

    if (this.value) {
      const intlNo = number ? this.phoneUtil.format(number, lpn.PhoneNumberFormat.INTERNATIONAL) : '';

      this.phoneNumberInformations = {
        number: this.value,
        internationalNumber: intlNo,
        nationalNumber: number ? this.phoneUtil.format(number, lpn.PhoneNumberFormat.NATIONAL) : '',
        e164Number: number ? this.phoneUtil.format(number, lpn.PhoneNumberFormat.E164) : '',
        countryCode: countryCode.toUpperCase(),
        dialCode: this.selectedCountry.dialCode,
      };
      this.onValueChanged.emit(this.phoneNumberInformations);
    }
  }

  public onCountrySelect(): void {
    this.mask = '';
    this.generateMaskFromNumber();

    if (this.value) {
      const number = this.getParsedNumber(this.value, this.selectedCountry.iso2);
      const intlNo = number ? this.phoneUtil.format(number, lpn.PhoneNumberFormat.INTERNATIONAL) : '';

      this.phoneNumberInformations = {
        number: this.value,
        internationalNumber: intlNo,
        nationalNumber: number ? this.phoneUtil.format(number, lpn.PhoneNumberFormat.NATIONAL) : '',
        e164Number: number ? this.phoneUtil.format(number, lpn.PhoneNumberFormat.E164) : '',
        countryCode: this.selectedCountry.iso2.toUpperCase(),
        dialCode: this.selectedCountry.dialCode,
      };
      this.onValueChanged.emit(this.phoneNumberInformations);
    }
  }

  private getLongestExampleNumberForCountry(iso2: string): lpn.PhoneNumber | null {
    let longestNumber: lpn.PhoneNumber | null = null;
    let maxLength = 0;

    const types = [
      lpn.PhoneNumberType.FIXED_LINE,
      lpn.PhoneNumberType.MOBILE,
      lpn.PhoneNumberType.FIXED_LINE_OR_MOBILE,
      lpn.PhoneNumberType.TOLL_FREE,
      lpn.PhoneNumberType.PREMIUM_RATE,
      lpn.PhoneNumberType.SHARED_COST,
      lpn.PhoneNumberType.VOIP,
      lpn.PhoneNumberType.PERSONAL_NUMBER,
      lpn.PhoneNumberType.PAGER,
      lpn.PhoneNumberType.UAN,
      lpn.PhoneNumberType.VOICEMAIL,
    ];

    for (const type of types) {
      const exampleNumber = this.phoneUtil.getExampleNumberForType(iso2, type);
      if (exampleNumber) {
        const length = exampleNumber.getNationalNumber().toString().length;
        if (length > maxLength) {
          maxLength = length;
          longestNumber = exampleNumber;
        }
      }
    }

    return longestNumber;
  }

  private isLengthVariable(iso2: string): boolean {
    const fixedLineLength = this.phoneUtil
      .getExampleNumberForType(iso2, lpn.PhoneNumberType.FIXED_LINE)
      .getNationalNumber()
      .toString().length;
    const mobileLength = this.phoneUtil
      .getExampleNumberForType(iso2, lpn.PhoneNumberType.MOBILE)
      .getNationalNumber()
      .toString().length;

    return fixedLineLength && mobileLength && fixedLineLength !== mobileLength;
  }

  private generateMaskFromNumber(): void {
    if (this.selectedCountry) {
      if (this.isLengthVariable(this.selectedCountry.iso2)) {
        const longestExampleNumber = this.getLongestExampleNumberForCountry(this.selectedCountry.iso2);
        if (longestExampleNumber) {
          const exampleNumberFormatted = this.phoneUtil.format(longestExampleNumber, lpn.PhoneNumberFormat.NATIONAL);
          this.mask = exampleNumberFormatted.replace(/\d/g, '#');
        } else {
          this.mask = 'Unknown format';
        }
      } else {
        const exampleNumber = this.phoneUtil.getExampleNumber(this.selectedCountry.iso2);
        if (exampleNumber) {
          const exampleNumberFormatted = this.phoneUtil.format(exampleNumber, lpn.PhoneNumberFormat.NATIONAL);
          this.mask = exampleNumberFormatted.replace(/\d/g, '#');
        } else {
          this.mask = 'Unknown format';
        }
      }
    }
  }

  private getParsedNumber(phoneNumber: string, countryCode: string): lpn.PhoneNumber {
    let number: lpn.PhoneNumber;
    try {
      number = this.phoneUtil.parse(phoneNumber, countryCode.toUpperCase());
    } catch (e) {}
    return number;
  }

  public handleChange(event) {
    this.selectedCountry = event.itemData;
    this.selectedCountryIndex = event.itemIndex;
    this.selectContrydropdown.instance.close();
    this.onCountrySelect();
  }

  private handleCountryValue(): void {
    this.selectedCountryIndex = 0;
    if (this.allCountries && this._dialCode) {
      this.selectedCountryIndex = this.allCountries.findIndex((country: Country) => country.dialCode === this.dialCode);
    }
    this.selectedCountry = this.allCountries[this.selectedCountryIndex > -1 ? this.selectedCountryIndex : 0];
  }
}
